import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Container,
  HeaderSection,
  HeaderSubTitle,
  HeaderTitle,
  ListContainer,
} from "../style";
import AdCard from "../../components/commonComponents/adCard";
import Header from "../../components/header";
import SearchAndFilter from "../../components/searchAndFilter";
import CreateBoardModal from "../../components/modals/createBoardModal";
import { SwipeFileReducerAction } from "../../store/swipeFile/slice";
import { useDispatch, useSelector } from "react-redux";
import { FilterOptions, SwipeFileListItems } from "../../store/swipeFile/types";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import SaveAdPopover from "../../components/saveAdPopover";
import {
  Box,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  BoardItem,
  listType,
  selectFolderBoardItem,
} from "../../store/metadata/types";
import CardListWrapper from "../../components/commonComponents/adCard/cardListWrapper";
import InfiniteScroll from "react-infinite-scroll-component";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import AddToBoardModal from "../../components/modals/addToBoardModal";
import AddTagModal from "../../components/modals/addTagModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  boardAdsFilterCountSelector,
  boardAdsItemsSelector,
  boardAdsSearchSelector,
  filterOptionSelector,
  isLoadingSelector,
  selectedBrandsSelector,
  selectedPlatformsSelector,
  selectedTabIndexSelector,
  selectedTagsSelector,
  selectFolderSelector,
} from "../../store/metadata/selector";
import { FolderIconDark } from "../../assets/Icons";
import { theme } from "../../utils/theme";
import { SwipeFileSagaActions } from "../../store/swipeFile/sagas";
import DeleteAdModal from "../../components/modals/deleteAdModal";
import { activeSubscriptionSelector } from "../../store/payment/selector";
import { StyledContainedButton } from "../../components/modals/style";
import routeConstant from "../../utils/routeConstant";

const MyFolder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { folderId } = useParams();
  const { pathname } = useLocation();
  const selectedFolder = useSelector(selectFolderSelector);
  const boardAdsItems = useSelector(boardAdsItemsSelector);
  const activePlan = useSelector(activeSubscriptionSelector);
  const finalBoardAdsItemsList = useMemo(
    () =>
      activePlan?.isFreePlan
        ? {
            data: boardAdsItems?.data?.slice(0, 5) || [],
            total: Math.min(boardAdsItems?.total || 0, 5),
          }
        : boardAdsItems,
    [activePlan?.isFreePlan, boardAdsItems]
  );

  const userDetails = JSON.parse(localStorage.getItem("User") as string);
  const selectedBrands = useSelector(selectedBrandsSelector);
  const selectedTabIndex = useSelector(selectedTabIndexSelector);
  const selectedPlatforms = useSelector(selectedPlatformsSelector);
  const selectedTags = useSelector(selectedTagsSelector);
  const isLoading = useSelector(isLoadingSelector);
  const filterOption = useSelector(filterOptionSelector);
  const boardAdsFilterCount = useSelector(boardAdsFilterCountSelector);
  const searchValue = useSelector(boardAdsSearchSelector);
  const folderIdValue = pathname.split("/")[2];
  const content = {
    tabTitle: selectedFolder?.name,
    tabSubTitle: "",
  };

  const setSwipeFileTabIndex = (tabIndex: number) => {
    dispatch(MetaDataReducerAction.setSelectedTabIndex(tabIndex));
    localStorage.setItem("boardTabIndex", String(tabIndex));
    dispatch(MetaDataReducerAction.resetSwipefileState());
    fetchFolderBoardAds(tabIndex);
  };

  const fetchSortingSwipeFileItems = (
    sortValue: string,
    sortObject: Object
  ) => {
    if (filterOption.sortValue !== sortValue) {
      dispatch(MetaDataReducerAction.setBoardAdsItems({ data: [] }));
      dispatch(MetaDataReducerAction.setFilterOptionSkipValue(0));
    }
    dispatch(
      MetaDataReducerAction.setSelectedSortOption({ sortValue, sortObject })
    );
    dispatch(
      MetaDataSagaActions.fetchFolderList({
        selectedFolderId: folderId ?? folderIdValue,
        selectedTabIndex,
        type: "filter",
      })
    );
  };

  const fetchfilterSwipeFileItems = (
    selectAll,
    checked,
    stateName,
    list,
    id
  ) => {
    dispatch(MetaDataReducerAction.setBoardAdsItems({ data: [], total: 0 }));
    dispatch(MetaDataReducerAction.setFilterOptionSkipValue(0));
    dispatch(
      MetaDataReducerAction.setSelectedFilterOptions({
        selectAll,
        checked,
        id,
        list,
        stateName,
      })
    );
    dispatch(
      MetaDataSagaActions.fetchFolderList({
        selectedFolderId: folderId ?? folderIdValue,
        selectedTabIndex,
        type: "filter",
      })
    );
  };

  const refetchSwipeFileItem = (
    adId: string,
    updatedBoardList: BoardItem,
    removeBoardIds: string
  ) => {
    const updatedSwipeFileItemData = finalBoardAdsItemsList?.data?.filter(
      (item: SwipeFileListItems) => {
        const isTargetAd = item?._id === adId;
        const hasMatchingBoard = item?.board?.some(
          (boardItem) =>
            removeBoardIds?.includes(boardItem?._id) &&
            boardItem?.folderId === folderId
        );
        if (isTargetAd && hasMatchingBoard) {
          return false; // remove this item
        }
        return true; // keep other items
      }
    );
    // fetchFolderBoardAds();
    dispatch(
      MetaDataReducerAction.setBoardAdsItems({ data: updatedSwipeFileItemData })
    );
    dispatch(
      SwipeFileReducerAction.setUpdatedBoardIds({ adId, updatedBoardList })
    );
  };

  const handleClearSwipeFileFilter = () => {
    dispatch(MetaDataReducerAction.resetSwipefileState());

    dispatch(
      MetaDataSagaActions.fetchFolderList({
        selectedFolderId: folderId ?? folderIdValue,
        selectedTabIndex: 0,
      })
    );
  };

  // ✅ useCallback ensures stable reference — avoids unnecessary re-creations
  const fetchFolderBoardAds = useCallback(
    (tabIndex?: number) => {
      if (!selectedFolder?.boards?.length) return;

      dispatch(MetaDataReducerAction.resetSwipefileState());

      const boardId = selectedFolder?.boards[tabIndex ?? selectedTabIndex]?._id;

      if (boardId) {
        dispatch(
          MetaDataSagaActions.fetchFolderListByBoardId({
            selectedFolderId: folderId ?? folderIdValue,
            selectedTabIndex: tabIndex ?? selectedTabIndex,
            boardId,
          })
        );
      }
    },
    [dispatch, folderId, folderIdValue, selectedFolder, selectedTabIndex]
  );

  useEffect(() => {
    if (folderIdValue && selectedFolder?.boards?.length > 0) {
      dispatch(MetaDataReducerAction.setIsLoading(true));
      fetchFolderBoardAds();
    }
  }, [
    folderIdValue,
    selectedFolder?.boards?.length,
    fetchFolderBoardAds,
    dispatch,
  ]);

  // useEffect(() => {
  //   const tabIndex = localStorage.getItem("boardTabIndex");
  //   dispatch(MetaDataReducerAction.setSelectedTabIndex(Number(tabIndex)));
  // })

  useEffect(() => {
    dispatch(MetaDataSagaActions.fetchFavouriteFolderList());
    dispatch(MetaDataSagaActions.fetchFavouriteBrands());
    dispatch(MetaDataSagaActions.fetchBoardList());
    dispatch(MetaDataSagaActions.fetchTagLists());
    return () => {
      dispatch(MetaDataReducerAction.resetSwipeSearchValue());
      dispatch(MetaDataReducerAction.setBrandFilterSearchText(""));
    };
  }, [dispatch]);

  const fetchMoreSwipeFileItem = useCallback(() => {
    dispatch(
      MetaDataSagaActions.fetchFolderList({
        selectedFolderId: folderId ?? folderIdValue,
        selectedTabIndex,
      })
    );
  }, [dispatch, folderId, folderIdValue, selectedTabIndex]);

  const handleSearchChange = async (value: string) => {
    if (!value) {
      await dispatch(MetaDataReducerAction.resetSkipLimitValue());
    }
    dispatch(MetaDataReducerAction.setBoardAdsItems({ data: [], total: 0 }));
    dispatch(MetaDataReducerAction.setBoardAdsSearchValue(value));
    dispatch(
      MetaDataSagaActions.fetchFolderList({
        selectedFolderId: folderId ?? folderIdValue,
        selectedTabIndex,
        type: "filter",
      })
    );
  };

  const hanldeSearchButton = () => {};

  const RenderAdsComponent = useMemo(() => {
    // const totalPages = Math.ceil(finalBoardAdsItemsList?.total / filterOption?.limit);
    // const currentPage = filterOption?.skip / filterOption?.limit;
    // const myItemsLength = finalBoardAdsItemsList?.data?.length ?? 0;
    // const hasMoreData = totalPages > currentPage;
    const swipeFileListItemsLength = finalBoardAdsItemsList?.data?.length ?? 0;
    const hasMoreData =
      finalBoardAdsItemsList?.total > swipeFileListItemsLength;

    if (selectedFolder?.boards?.length === 0) {
      return (
        <ListContainer>
          <HeaderTitle textAlign="center">
            Create your first board and Save Ads
          </HeaderTitle>
          <HeaderSubTitle textAlign="center">
            Add Multiple Boards in Folders and enjoy organizing Ads.
          </HeaderSubTitle>
        </ListContainer>
      );
    }

    // Show loading if data is being fetched or folder is not loaded yet
    if (isLoading || !selectedFolder || !finalBoardAdsItemsList) {
      return (
        <ListContainer>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        </ListContainer>
      );
    }

    if (!isLoading && swipeFileListItemsLength === 0) {
      return (
        <ListContainer>
          <HeaderTitle textAlign="center">No ads found</HeaderTitle>
          <HeaderSubTitle textAlign="center">
            Use our extension to save ads from platforms and grow
          </HeaderSubTitle>
        </ListContainer>
      );
    }

    return (
      <ListContainer>
        <CardListWrapper>
          {finalBoardAdsItemsList?.data?.map(
            (item: SwipeFileListItems, index: number) => (
              <AdCard
                item={item}
                key={item._id}
                dataList={finalBoardAdsItemsList?.data}
                locationPathname={""}
                isBoardAdsData={true}
              />
            )
          )}
          {isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <AdCard item={{} as SwipeFileListItems} key={`sk-${i}`} />
            ))}
        </CardListWrapper>
        <InfiniteScroll
          scrollableTarget={document.getElementById("content") as ReactNode}
          dataLength={swipeFileListItemsLength}
          // dataLength={finalBoardAdsItemsList?.total}
          next={fetchMoreSwipeFileItem}
          hasMore={hasMoreData}
          loader={
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          }
          style={{ overflow: "hidden" }}
        >
          <></>
        </InfiniteScroll>
      </ListContainer>
    );
  }, [
    finalBoardAdsItemsList,
    isLoading,
    selectedFolder,
    fetchMoreSwipeFileItem,
  ]);

  const handleTabs = (event: React.SyntheticEvent, newValue: number) => {
    setSwipeFileTabIndex(newValue);
  };

  return (
    <Container>
      <HeaderSection>
        <Header content={content} />
        <SearchAndFilter
          handleFilter={fetchfilterSwipeFileItems}
          selectedBrands={selectedBrands}
          selectedPlatforms={selectedPlatforms}
          handleSortFilter={fetchSortingSwipeFileItems}
          selectedFilterOption={filterOption?.sortValue}
          renderBoardFilter={false}
          searchValue={searchValue}
          handleSearchChange={handleSearchChange}
          hanldeSearchButton={hanldeSearchButton}
          buttonsToShow={["filter", "sort"]}
          filterCounts={boardAdsFilterCount}
          handleClearFilter={handleClearSwipeFileFilter}
          userId={userDetails?._id}
          renderTagFilter={true}
          selectedTags={selectedTags}
          placeholder={`Search in the ${selectedFolder?.name || "folder"}...`}
        />
        <Box display="flex" alignItems="center">
          <Box
            sx={{
              display: "flex",
              paddingX: "20px",
              gap: "5px",
              alignItems: "center !important",
              justifyContent: "center",
            }}
          >
            <FolderIconDark />
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#414042" }}
            >
              Boards:
            </Typography>
          </Box>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ m: 0 }}
          />
          {selectedFolder?.boards?.length > 0 && (
            <Tabs
              value={selectedTabIndex}
              onChange={handleTabs}
              variant="scrollable"
              // scrollButtons={false}
              sx={{
                ".MuiTabs-flexContainer": { gap: "5rem" },
                minHeight: "3rem",
                "& .MuiTabs-scroller": {
                  overflowX: "auto",
                  scrollbarWidth: "none",
                  "-ms-overflow-style": "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  "-webkit-overflow-scrolling": "touch",
                },
              }}
            >
              {selectedFolder?.boards?.map((item: selectFolderBoardItem) => (
                <Tab
                  key={item?._id}
                  label={item?.name}
                  sx={{
                    "&.Mui-selected": {
                      color: theme.colors.blue,
                      borderBottom: "1px solid",
                      fontWeight: "600",
                    },
                    color: theme.colors.gray1,
                    textTransform: "capitalize",
                    paddingBottom: "1rem",
                    minWidth: "fit-content",
                    fontSize: "1rem",
                    minHeight: "3rem",
                  }}
                />
              ))}
            </Tabs>
          )}
          {selectedFolder?.boards?.length === 0 && (
            <Typography
              sx={{ fontSize: "14px", color: "#414042", marginLeft: "8px" }}
            >
              No Boards Found
            </Typography>
          )}
        </Box>

        {activePlan?.isFreePlan && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: theme.colors.lightBlue,
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              marginBottom: "1rem",
            }}
          >
            <Typography
              variant="subtitle2"
              color={theme.colors.white}
              fontWeight={700}
              sx={{ flex: 1, mr: 2 }}
            >
              You are currently on the Free plan. Only 5 ads are visible.
              Upgrade your plan to see the full list.
            </Typography>

            <StyledContainedButton
              variant="contained"
              size="small"
              sx={{
                backgroundColor: theme.colors.white,
                color: theme.colors.lightBlue,
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: theme.colors.white,
                  opacity: 0.9,
                },
              }}
              onClick={() => {
                navigate(`${routeConstant.subscription}`);
              }}
            >
              Upgrade Plan
            </StyledContainedButton>
          </Box>
        )}
        {RenderAdsComponent}
      </HeaderSection>
      <SaveAdPopover reFetchData={refetchSwipeFileItem} />
      <CreateBoardModal />
      <AddToBoardModal />
      <AddTagModal />
      <DeleteAdModal
        handleDelete={async () => {
          await dispatch(SwipeFileSagaActions.deleteAd());
          fetchFolderBoardAds();
        }}
      />
    </Container>
  );
};

export default MyFolder;
